import { serverSideConfigs, t } from 'localization'
import drop from 'lodash/drop'
import join from 'lodash/join'
import getConfig from 'next/config'
import Head from 'next/head'
import ReactMarkdown from 'react-markdown'
import { serverRequestService } from 'server/services/serverRequestService'
import { createMosaicClient } from 'server/utils/mosaicClient'
import Image from 'shared/components/Image'
import { PagesQA } from 'shared/dataAttributes'
import { useWindowSize } from 'shared/hooks/useWindowSize'
import { colors, fonts, media, styles } from 'shared/lib'
import { sizes } from 'shared/lib/media'
import { mediaQueries } from 'shared/theme'
import { Store } from 'shared/types/Store'
import { startCase } from 'shared/utils/formatting-utils'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import { getStoreImageUrl, STORE_BY_URL, STORE_DETAILS, StoreID } from 'shared/utils/store-utils'
import FlightClubBrands from 'stores/components/FlightClubBrands'
import FlightClubStoreBreadcrumbs from 'stores/components/FlightClubStoreBreadcrumbs'
import styled from 'styled-components'

const TP = 'pages.storelocator.store'
const BY_APPOINTMENT = 'by appointment only'

type StoreDetailProps = {
  store: Store
  storeImages: { [key: string]: string }
}

const NEW_LINE_RESPONSE = '\r\n'
const NEW_LINE_MARKDOWN = '  \r'

const formatSchema = (store: Store, isMobile: boolean, storeImages: { [key: string]: string }) => {
  const storeName = startCase(store.title.toLowerCase())
  const locality = store.address2.split(',')[0]
  const addressDetails = store.address2.split(' ')
  const postalCode = addressDetails.pop()
  const region = addressDetails.pop()

  return JSON.stringify({
    '@context': 'http://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': 'https://www.flightclub.com',
              name: 'Flight Club',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': 'https://www.flightclub.com/storelocator',
              name: 'Flight Club Stores',
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@id': `https://www.flightclub.com/storelocator/${
                STORE_DETAILS[store.id as StoreID]?.url
              }`,
              name: `Flight Club ${storeName}`,
            },
          },
        ],
      },
      {
        '@type': 'Store',
        name: `Flight Club ${storeName}`,
        image: [`https://www.flightclub.com${getStoreImageUrl(store.id, isMobile, storeImages)}`],
        address: {
          '@type': 'PostalAddress',
          streetAddress: store.address1,
          addressLocality: locality,
          addressRegion: region,
          postalCode: postalCode,
          addressCountry: store.id === StoreID.TYO ? 'JP' : 'US',
          phoneNumber: store.phoneNumber,
        },
      },
    ],
  })
}

const getHoursWeekday = (hoursWeekday: string) => {
  const daysMap = new Map<string, string>()

  if (hoursWeekday) {
    const days = hoursWeekday.split(NEW_LINE_RESPONSE)

    days.forEach((day) => {
      const dayDetails = day.split(' ')
      daysMap.set(dayDetails[0], join(drop(dayDetails), ' '))
    })
  }

  return daysMap
}

const StoreDetail = ({ store, storeImages }: StoreDetailProps) => {
  const { isMobile } = useWindowSize()

  const storeHoursWeekday = getHoursWeekday(store.storeHoursWeekday)
  const consignmentHoursWeekday = getHoursWeekday(store.consignmentHoursWeekday)

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: formatSchema(store, isMobile, storeImages),
          }}
        />
        <title>{`Flight Club ${startCase(store.title.toLowerCase())}`}</title>
        <meta name="description" content={store.aboutCopy} />
      </Head>
      <Layout>
        <Wrapper>
          <DesktopBreadcrumbsWrapper>
            <FlightClubStoreBreadcrumbs storeName={store.title} />
          </DesktopBreadcrumbsWrapper>
          <GridHeader data-qa={PagesQA.StoresPageTitle}>
            <SubHeader>
              <Title>
                <div>{t(`${TP}.flightclub`, 'Flight Club')}</div>
                <div>{store.title}</div>
              </Title>
            </SubHeader>
            <SubHeader>
              <StoreAddressTitle>{t(`${TP}.storeAddress`, 'Address')}</StoreAddressTitle>
              <StoreAddress>
                <AddressLink target="_blank" href={store.mapUrl} rel="noopener noreferrer">
                  <StoreAddressText>{store.address1}</StoreAddressText>
                  <StoreAddressText>{store.address2}</StoreAddressText>
                </AddressLink>
                {store.phoneNumber && (
                  <StoreAddressText>
                    {isMobile ? (
                      <a href={`tel:${store.phoneNumber.replace(/\s+/g, '')}`}>
                        {store.phoneNumber}
                      </a>
                    ) : (
                      store.phoneNumber
                    )}
                  </StoreAddressText>
                )}
              </StoreAddress>
            </SubHeader>
          </GridHeader>
          <Body>
            <ImageContainer>
              <StyledImage
                src={getStoreImageUrl(store.id, isMobile, storeImages)}
                alt="Flight Club Stores"
                sizes={`(min-width: ${sizes.medium}px) 500px, 100vw`}
              />
            </ImageContainer>
            <StoreDetails>
              <div>
                {store.storeHoursWeekday && (
                  <Box>
                    <StoreHoursTitle>{t(`${TP}.storeHours`, 'Store Hours')}</StoreHoursTitle>
                    <StoreHoursDetails>
                      {store.id === StoreID.TYO &&
                      store.storeHoursWeekday.toLowerCase() === BY_APPOINTMENT ? (
                        <StoreHoursGrid>
                          <StoreHours>{store.storeHoursWeekday}</StoreHours>
                        </StoreHoursGrid>
                      ) : (
                        Array.from(storeHoursWeekday.keys()).map((key) => (
                          <StoreHoursGrid key={key}>
                            <StoreHours>{key}</StoreHours>
                            <StoreHours $right={true}>{storeHoursWeekday.get(key)}</StoreHours>
                          </StoreHoursGrid>
                        ))
                      )}
                    </StoreHoursDetails>
                  </Box>
                )}
                {store.consignmentHoursWeekday && (
                  <Box>
                    <StoreHoursTitle>{t(`${TP}.consignment`, 'Consignment')}</StoreHoursTitle>
                    <StoreHoursDetails>
                      {store.consignmentAddress1 && (
                        <ConsignmentAddress data-qa={PagesQA.ConsignmentAddress}>
                          <StoreAddressText>{store.consignmentAddress1}</StoreAddressText>
                          {store.consignmentAddress2 && (
                            <StoreAddressText>{store.consignmentAddress2}</StoreAddressText>
                          )}
                        </ConsignmentAddress>
                      )}
                      {Array.from(consignmentHoursWeekday.keys()).map((key) => (
                        <StoreHoursGrid key={key}>
                          <StoreHours>{key}</StoreHours>
                          <StoreHours $right={true}>{consignmentHoursWeekday.get(key)}</StoreHours>
                        </StoreHoursGrid>
                      ))}
                    </StoreHoursDetails>
                  </Box>
                )}
                {store.customMessage && (
                  <Box>
                    <StoreMessage>
                      <ReactMarkdown linkTarget="_blank">
                        {store.customMessage.replace(NEW_LINE_RESPONSE, NEW_LINE_MARKDOWN)}
                      </ReactMarkdown>
                    </StoreMessage>
                  </Box>
                )}
              </div>
              {store.aboutCopy && (
                <div>
                  <StoreHoursTitle>
                    {t(`${TP}.aboutStore`, {
                      defaultValue: 'About Flight Club {storeTitle}',
                      storeTitle: store.title,
                    })}
                  </StoreHoursTitle>
                  <Paragraph>
                    <ReactMarkdown>
                      {store.aboutCopy.replace(NEW_LINE_RESPONSE, NEW_LINE_MARKDOWN)}
                    </ReactMarkdown>
                  </Paragraph>
                </div>
              )}
            </StoreDetails>
          </Body>
        </Wrapper>
        <FlightClubBrands />
        <MobileBreadcrumbsWrapper>
          <FlightClubStoreBreadcrumbs storeName={store.title} />
        </MobileBreadcrumbsWrapper>
      </Layout>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { res, req, locale, query } = context
  const storeID = STORE_BY_URL[query.store as keyof typeof STORE_BY_URL]

  try {
    const { serverRuntimeConfig } = getConfig()
    const { mosaicRaffleNamespace } = serverRuntimeConfig
    const mosaicClient = createMosaicClient(req, locale)
    const { data: storeImagesData } = await mosaicClient.get(
      `/api/v1/mosaic/${mosaicRaffleNamespace}/store-images`,
    )
    const { data } = await serverRequestService(req, res, 'sneakers').get(
      '/api/v1/flightclub_stores',
    )
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        store: data.flightclubStores.find((fcStore: Store) => fcStore.id === storeID),
        storeImages: storeImagesData.images,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        notFound: true,
      },
    }
  }
}

const Layout = styled.div`
  background-color: ${colors.FC2_WHITE};
  display: grid;
`

const Wrapper = styled.div`
  height: 100%;
  padding: ${styles.mobilePageLayoutPadding};
  ${media.large`
    max-width: 1500px;
    place-self: center;
    width: 100%;
    padding: 40px 150px;
  `}
`

const GridHeader = styled.div`
  margin-top: 28px;
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr;
  ${media.large`
    margin-top: 40px;
    grid-template-columns: 1fr 1fr;
  `}
`

const SubHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
`

const Title = styled.h1`
  ${fonts.HEADER_2}
  font-weight: 500;
  font-size: 24px;
  line-height: 90%;
  text-align: left;
  margin: 0;
  width: 100%;
  ${media.large`
    font-size: 32px;
  `}
`

const Paragraph = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_DARK_GREY};
  line-height: 140%;
  text-align: left;
  font-weight: 400;
  font-size: 14px;
  margin: 0;
  place-self: start;
  ${media.large`
    place-self: end;
  `}
`

const StoreAddressTitle = styled.h2`
  ${fonts.SUBTITLE_2}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.4px;
  text-align: left;
  margin: 0 0 12px;
  width: 300px;
  place-self: start;
  ${media.large`
    place-self: end;
  `}
`

const StoreAddress = styled.h2`
  ${fonts.SUBTITLE_2}
  line-height: 140%;
  color: ${colors.FC2_DARK_GREY};
  text-align: left;
  font-weight: 400;
  text-transform: none;
  margin: 0;
  width: 300px;
  place-self: start;
  ${media.large`
    place-self: end;
  `}
`

const StoreAddressText = styled.div`
  display: block;
`

const StoreHoursTitle = styled.h2`
  ${fonts.SUBTITLE_2}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.4px;
  text-align: left;
  margin-top: 40px;
`

const StoreHours = styled.p<{ $right?: boolean }>`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_DARK_GREY};
  line-height: 140%;
  text-align: left;
  font-weight: 400;
  font-size: 14px;
  margin: 0 0 2px;
  ${({ $right }) => $right && 'justify-self: right;'};
  ${media.large`
    justify-self: unset;
  `}
`

const ConsignmentAddress = styled.div`
  ${fonts.LINK_ACTION}
  line-height: 140%;
  font-weight: 400;
  margin-bottom: 12px;
`

const MobileBreadcrumbsWrapper = styled.div`
  background-color: ${colors.FC2_WHITE};
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  width: 100%;
  padding: ${styles.mobilePageLayoutPadding};
  margin: 0 20px 0 0;
  display: flex;
  flex-direction: row;
  place-content: center flex-start;
  overflow-wrap: break-word;
  ${mediaQueries.desktop} {
    display: none;
  }
`

const DesktopBreadcrumbsWrapper = styled.div`
  display: none;
  ${mediaQueries.desktop} {
    padding: 0;
    margin: 0 20px 0 0;
    display: flex;
    flex-direction: row;
    place-content: center flex-start;
    overflow-wrap: break-word;
    max-width: 600px;
  }
`

const Body = styled.div`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr;
  margin-bottom: 20px;
  margin-top: 40px;
`

const StoreDetails = styled.div`
  display: grid;
  grid-gap: 0;
  grid-template-columns: 1fr;
  padding-top: 16px;
  ${media.large`
    grid-template-columns: 1fr 1fr;
    grid-gap: 24px;
    padding: 16px 80px 0px;
  `}
  margin-bottom: 20px;
`

const ImageContainer = styled.div`
  position: relative;
  object-fit: contain;
  max-height: 714px;
  height: 450px;
  ${media.large`
    height: 750px;
  `}
`

const StyledImage = styled(Image)`
  object-fit: cover;
  object-position: 30% 90%;
`

const Box = styled.div`
  padding-bottom: 40px;
  border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
  ${media.large`
    border-bottom: 0;
    padding-bottom: 0;
  `}
`

const StoreHoursDetails = styled.div`
  margin-top: 12px;
`

const StoreMessage = styled.div`
  ${fonts.SUBTITLE_3}
  font-weight: 400;
  font-size: 14px;
  margin-top: 40px;
  ${media.large`
    margin-top: 15px;
  `}
`

const StoreHoursGrid = styled.div`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr 1fr;
`

const AddressLink = styled.a`
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export default StoreDetail
